/**
 * All Tab — full challenge discovery: banners, categories, sections, list.
 * Spec: challenges .txt § 4 — Challenge Discovery.
 */

import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { AdBannerCarousel } from './ad-banner-carousel'
import { CategoryChips } from './category-chips'
import { BrowseChallengeCard } from './browse-challenge-card'
import { useBrowseChallenges } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'

type SortMode = 'popular' | 'newest' | 'rating'

export const AllTab = memo(function AllTab(): React.ReactElement {
  const { t } = useTranslation()
  const [category, setCategory] = useState<string | null>(null)
  const [sort, setSort] = useState<SortMode>('popular')
  const [page, setPage] = useState(0)

  const { data: challenges, isLoading } = useBrowseChallenges({
    category,
    sort,
    page,
  })

  const handleLoadMore = useCallback((): void => {
    setPage((p) => p + 1)
  }, [])

  return (
    <div className="space-y-4">
      {/* Ad banners */}
      <AdBannerCarousel />

      {/* Category chips */}
      <CategoryChips selected={category} onSelect={(id) => { setCategory(id); setPage(0) }} />

      {/* Sort tabs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[14px] font-semibold text-text">
            {t('challenges.browse.allChallenges')}
          </h3>
          <div className="flex gap-1">
            {(['popular', 'newest', 'rating'] as const).map((s) => (
              <button
                key={s}
                onClick={() => { setSort(s); setPage(0) }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors tap-highlight-transparent ${
                  sort === s
                    ? 'bg-primary/15 text-primary'
                    : 'text-text-hint hover:bg-white/[0.04]'
                }`}
              >
                {t(`challenges.browse.sort.${s}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Challenge list */}
        {isLoading && page === 0 ? (
          <div className="space-y-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[100px] rounded-2xl" />
            ))}
          </div>
        ) : (challenges?.length ?? 0) === 0 ? (
          <EmptyState
            icon={null}
            title={t('challenges.browse.noResults')}
            description={t('challenges.browse.noResultsDesc')}
          />
        ) : (
          <>
            <div className="space-y-2.5">
              {(challenges ?? []).map((c) => (
                <BrowseChallengeCard key={c.id} challenge={c} />
              ))}
            </div>

            {(challenges?.length ?? 0) >= 20 && (
              <button
                onClick={handleLoadMore}
                className="w-full mt-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-text-hint font-medium hover:bg-white/[0.05] transition-colors tap-highlight-transparent"
              >
                {t('challenges.browse.loadMore')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
})
